(function () {
  'use strict';

  const LETTER_CHARMS_PATH = '/products/letter-charms';
  const OPTION_KEY_PATTERN = /^option[1-3]$/;
  const FORM_SELECTOR = '#add-to-cart-form #product-variants';
  const WRAPPER_SELECTOR = '.selector-wrapper';
  const SELECT_SELECTOR = 'select.single-option-selector';
  const COMPONENT_CLASS = 'jt-variant-image-selector';
  const NATIVE_HIDDEN_CLASS = 'jt-variant-image-selector-native';
  const productDataCache = new Map();
  const selectObservers = new WeakMap();
  const pathname = window.location.pathname;
  const handleMatch = pathname.match(/\/products\/([^/]+)(?:\/|$)/i);

  if (!handleMatch) return;

  const handle = decodeURIComponent(handleMatch[1]);
  const targetLabel = pathname.includes(LETTER_CHARMS_PATH) ? 'letter' : 'color';
  let initializationQueued = false;

  function getOptionSignature(select) {
    return Array.from(select.options, function (option) {
      return option.value + ':' + (option.disabled ? '1' : '0');
    }).join('|');
  }

  function findTarget() {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return null;

    const wrappers = Array.from(form.querySelectorAll(WRAPPER_SELECTOR));
    const wrapper = wrappers.find(function (candidate) {
      const label = candidate.querySelector('label');
      return label && label.textContent.trim().toLowerCase().includes(targetLabel);
    });

    if (!wrapper) return null;

    const label = wrapper.querySelector('label');
    const select = wrapper.querySelector(SELECT_SELECTOR);
    if (!label || !select) return null;

    const optionKey = select.dataset.option;
    if (!OPTION_KEY_PATTERN.test(optionKey)) return null;

    return {
      wrapper: wrapper,
      label: label,
      select: select,
      optionKey: optionKey
    };
  }

  function loadProductData() {
    if (productDataCache.has(handle)) return productDataCache.get(handle);

    const productUrl = `/products/${encodeURIComponent(handle)}.js`;
    const request = fetch(productUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to load product data');
        return response.json();
      })
      .then(function (product) {
        if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
        if (!Array.isArray(product.images) || !product.images.length) return null;
        return product;
      })
      .catch(function () {
        return null;
      });

    productDataCache.set(handle, request);
    return request;
  }

  function getFirstProductImage(product) {
    const firstImage = product.images[0];
    if (typeof firstImage === 'string') return firstImage;
    return firstImage && firstImage.src ? firstImage.src : '';
  }

  function getSizedImageUrl(src) {
    if (!src || typeof src !== 'string') return '';

    const queryIndex = src.indexOf('?');
    const base = queryIndex === -1 ? src : src.slice(0, queryIndex);
    const query = queryIndex === -1 ? '' : src.slice(queryIndex);
    const extensionMatch = base.match(/\.(jpe?g|png|webp|gif)$/i);
    if (!extensionMatch) return src;

    const extension = extensionMatch[0];
    return base.slice(0, -extension.length) + '_120x' + extension + query;
  }

  function createImageMap(product, optionKey) {
    const imageMap = new Map();

    product.variants.forEach(function (variant) {
      const value = variant[optionKey];
      const imageSrc = variant.featured_image && variant.featured_image.src;
      if (value && imageSrc && !imageMap.has(value)) imageMap.set(value, imageSrc);
    });

    return imageMap;
  }

  function syncSelectedState(component, select) {
    if (!component || !select) return;

    const selectedValue = select.value;
    const valueNode = component.querySelector('.jt-variant-image-selector__value');
    if (valueNode) valueNode.textContent = selectedValue;

    component.querySelectorAll('.jt-variant-image-selector__option').forEach(function (button) {
      const isSelected = button.dataset.optionValue === selectedValue;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  function restoreNativeSelector(wrapper, component) {
    if (component && component.parentNode) component.parentNode.removeChild(component);
    if (!wrapper) return;
    wrapper.classList.remove(NATIVE_HIDDEN_CLASS);
    wrapper.dataset.jtVariantImageSelector = 'failed';
  }

  function buildComponent(target, product) {
    const fallbackSource = getFirstProductImage(product);
    const fallbackThumbnail = getSizedImageUrl(fallbackSource);
    const imageMap = createImageMap(product, target.optionKey);
    const options = Array.from(target.select.options);

    if (!options.length || !fallbackSource) return null;

    const component = document.createElement('div');
    component.className = COMPONENT_CLASS;
    component.dataset.optionKey = target.optionKey;
    component.dataset.optionSignature = getOptionSignature(target.select);

    const labelRow = document.createElement('div');
    labelRow.className = 'jt-variant-image-selector__label';

    const labelText = document.createElement('span');
    labelText.className = 'jt-variant-image-selector__name';
    labelText.textContent = target.label.textContent.trim();

    const selectedValue = document.createElement('span');
    selectedValue.className = 'jt-variant-image-selector__value';
    selectedValue.textContent = target.select.value;

    labelRow.appendChild(labelText);
    labelRow.appendChild(selectedValue);
    component.appendChild(labelRow);

    const optionRow = document.createElement('div');
    optionRow.className = 'jt-variant-image-selector__options';

    options.forEach(function (option) {
      const value = option.value;
      const mappedSource = imageMap.get(value) || fallbackSource;
      const thumbnailSource = getSizedImageUrl(mappedSource);
      if (!thumbnailSource) throw new Error('Missing option image');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jt-variant-image-selector__option';
      button.dataset.optionValue = value;
      button.setAttribute('aria-label', `Select ${value}`);
      button.setAttribute('aria-pressed', 'false');
      button.disabled = option.disabled;

      const image = document.createElement('img');
      image.src = thumbnailSource;
      image.alt = value;
      image.width = 66;
      image.height = 66;
      image.setAttribute('loading', 'lazy');
      image.dataset.fallbackSrc = fallbackThumbnail;
      image.dataset.usedFallback = mappedSource === fallbackSource ? 'true' : 'false';
      image.addEventListener('error', function () {
        if (image.dataset.usedFallback !== 'true' && fallbackThumbnail) {
          image.dataset.usedFallback = 'true';
          image.src = fallbackThumbnail;
          return;
        }
        restoreNativeSelector(target.wrapper, component);
      });

      button.appendChild(image);
      optionRow.appendChild(button);
    });

    component.appendChild(optionRow);
    return component;
  }

  function insertComponent(target, component) {
    const fragment = document.createDocumentFragment();
    fragment.appendChild(component);
    target.wrapper.appendChild(fragment);
    target.wrapper.classList.add(NATIVE_HIDDEN_CLASS);
    target.wrapper.dataset.jtVariantImageSelector = 'ready';
    syncSelectedState(component, target.select);
  }

  function enhanceTarget() {
    const target = findTarget();
    if (!target) return Promise.resolve();

    observeSelectOptions(target.select);

    if (target.wrapper.dataset.jtVariantImageSelector === 'failed') return Promise.resolve();

    const existing = target.wrapper.querySelector('.' + COMPONENT_CLASS);
    const signature = getOptionSignature(target.select);

    if (existing && existing.dataset.optionSignature === signature) {
      syncSelectedState(existing, target.select);
      return Promise.resolve();
    }

    if (target.wrapper.dataset.jtVariantImageSelector === 'loading') return Promise.resolve();
    target.wrapper.dataset.jtVariantImageSelector = 'loading';

    return loadProductData().then(function (product) {
      if (!product) {
        restoreNativeSelector(target.wrapper, existing);
        return;
      }

      try {
        const component = buildComponent(target, product);
        if (!component) {
          restoreNativeSelector(target.wrapper, existing);
          return;
        }

        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        insertComponent(target, component);
      } catch (error) {
        restoreNativeSelector(target.wrapper, existing);
      }
    });
  }

  function queueInitialization() {
    if (initializationQueued) return;
    initializationQueued = true;

    Promise.resolve().then(function () {
      initializationQueued = false;
      return enhanceTarget();
    }).catch(function () {
      initializationQueued = false;
    });
  }

  function observeSelectOptions(select) {
    if (selectObservers.has(select)) return;

    const observer = new MutationObserver(queueInitialization);
    observer.observe(select, { childList: true });
    selectObservers.set(select, observer);
  }

  function elementTouchesProductOptions(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches(FORM_SELECTOR) || node.matches(WRAPPER_SELECTOR)) return true;
    return Boolean(node.querySelector(FORM_SELECTOR + ', ' + WRAPPER_SELECTOR));
  }

  function mutationAffectsProductOptions(record) {
    return Array.from(record.addedNodes).some(elementTouchesProductOptions) ||
      Array.from(record.removedNodes).some(elementTouchesProductOptions);
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('.jt-variant-image-selector__option');
    if (!button || button.disabled) return;

    const component = button.closest('.' + COMPONENT_CLASS);
    const wrapper = component && component.closest(WRAPPER_SELECTOR);
    const select = wrapper && wrapper.querySelector(SELECT_SELECTOR);
    if (!select) return;

    select.value = button.dataset.optionValue;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncSelectedState(component, select);
  });

  function handleNativeChange(event) {
    const select = event.target.closest ? event.target.closest(SELECT_SELECTOR) : null;
    if (!select) return;

    const wrapper = select.closest(WRAPPER_SELECTOR);
    const component = wrapper && wrapper.querySelector('.' + COMPONENT_CLASS);
    if (component) syncSelectedState(component, select);
  }

  if (window.jQuery) {
    window.jQuery(document)
      .off('change.jtVariantImageSelector', SELECT_SELECTOR)
      .on('change.jtVariantImageSelector', SELECT_SELECTOR, handleNativeChange);
  } else {
    document.addEventListener('change', handleNativeChange);
  }

  function initialize() {
    const observerRoot = document.querySelector('#MainContent') || document.body;
    if (!observerRoot) return;

    const observer = new MutationObserver(function (records) {
      if (records.some(mutationAffectsProductOptions)) queueInitialization();
    });
    observer.observe(observerRoot, { childList: true, subtree: true });
    queueInitialization();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
